import React from 'react';
import TableRow from "@mui/material/TableRow";
import Skeleton from '@mui/material/Skeleton';
import TableCell from "@mui/material/TableCell";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSelector } from 'react-redux';
import { useHistory } from '../../../../utils/reactRouterCompatibility';
import { getSupportBoxTicketRoute } from "../../../../../router/routes/support/factory/boxes";
import { styled, useTheme } from '@mui/material/styles';

const StyledTableRow = styled(TableRow, {
    shouldForwardProp: prop => prop !== 'isSelected'
})(({ theme, isSelected }) => ({
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    backgroundColor: isSelected ? 'rgba(255, 145, 0, 0.12)' : 'transparent',
    '&:hover': {
        backgroundColor: isSelected ? 'rgba(255, 145, 0, 0.18)' : 'rgba(255, 255, 255, 0.05)',
    },
}));

const TimeText = styled(Typography, {
    shouldForwardProp: prop => prop !== 'urgency'
})(({ theme, urgency }) => ({
    fontSize: '0.75rem',
    ...(urgency === 'ok' && {
        color: theme.palette.text.secondary,
    }),
    ...(urgency === '2days' && {
        color: theme.palette.text.primary,
    }),
    ...(urgency === '5days' && {
        color: theme.palette.warning.main,
    }),
    ...(urgency === 'urgent' && {
        color: theme.palette.error.main,
        fontWeight: 600,
    }),
}));

const Row = ({ ticket, loading, box }) => {
    const selected = useSelector(state => state.support.ticket);
    const displayTicket = (!loading && ticket);
    const { t } = useTranslation();
    const history = useHistory();
    const theme = useTheme();

    const isSelected = (selected && !loading) ? selected.Support_Ticket__ === ticket.Support_Ticket__ : false;

    const handleSelect = () => {
        if (isSelected) return;
        history.push(getSupportBoxTicketRoute(box, ticket.Support_Ticket__));
    };

    const getUrgency = () => {
        if (!ticket?.Last_Update?.unix) return 'ok';
        const now = moment().unix();
        const diff = now - ticket.Last_Update.unix;

        if (diff < 86400) return 'ok';        // 24h
        if (diff < 86400 * 2) return '2days'; // 2 days
        if (diff < 86400 * 5) return '5days'; // 5 days
        return 'urgent';
    };

    return (
        <StyledTableRow isSelected={isSelected} onClick={handleSelect}>
            <TableCell sx={{ py: 1.5 }}>
                {!displayTicket && <Skeleton />}
                {displayTicket && (
                    <Grid container spacing={0.5}>
                        <Grid item xs={8}>
                            {!ticket.User.Email && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('support_ticket_anonymous')}
                                </Typography>
                            )}
                            {ticket.User.Profile.Display_Name && (
                                <Typography variant="body2" color="text.secondary">
                                    {ticket.User.Profile.Display_Name}
                                </Typography>
                            )}
                            {ticket.User.Email && (
                                <Typography variant="body2" color="text.secondary">
                                    {ticket.User.Email}
                                </Typography>
                            )}
                        </Grid>

                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <TimeText urgency={getUrgency()}>
                                {moment(parseInt(ticket.Last_Update.unixms)).fromNow()}
                            </TimeText>
                        </Grid>

                        <Grid item xs={8}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                {ticket.Subject}
                            </Typography>
                        </Grid>

                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                                {t('support_ticket_message_count', { messageCount: ticket.Message_Count })}
                            </Typography>
                        </Grid>
                    </Grid>
                )}
            </TableCell>
        </StyledTableRow>
    );
};

export default Row;